import type { IMarket } from "~/types/event";
import type { Platform } from "~/types/platform";
import { joinWithAnd, listPlatformNames } from "~/utils/joinWithAnd";

import type { MarketResult } from "./getMarketSettlement";
import {
  getSettledElsewhere,
  type ISettledElsewhere,
  type ISettledGroup,
} from "./getSettledElsewhere";
import { RESULT_WORD } from "./resolutionLabels";

interface IResultGroup extends ISettledGroup {
  result: MarketResult;
}

const STILL_OPEN_ONE = "has not resolved yet and settles by its own rules";
const STILL_OPEN_MANY = "have not resolved yet and settle by their own rules";
const RESULT_NOT_AVAILABLE = "(result not available)";

const hasResult = (group: ISettledGroup): group is IResultGroup =>
  group.result !== null;

const describeResults = (settled: readonly ISettledGroup[]): string =>
  joinWithAnd(
    settled
      .filter(hasResult)
      .map(
        ({ result, platforms }) =>
          `${RESULT_WORD[result]} on ${listPlatformNames(platforms)}`
      )
  );

const describeNoResult = (settled: readonly ISettledGroup[]): string => {
  const group = settled.find(({ result }) => result === null);

  return group
    ? `on ${listPlatformNames(group.platforms)} ${RESULT_NOT_AVAILABLE}`
    : "";
};

const describeSettled = (settled: readonly ISettledGroup[]): string =>
  [describeResults(settled), describeNoResult(settled)]
    .filter(Boolean)
    .join(", and ");

const describeOpen = (platforms: readonly Platform[]): string =>
  `${listPlatformNames(platforms)} ${platforms.length > 1 ? STILL_OPEN_MANY : STILL_OPEN_ONE}`;

export const formatSettledElsewhere = ({
  settled,
  openPlatforms,
}: ISettledElsewhere): string =>
  `Already settled ${describeSettled(settled)}. ${describeOpen(openPlatforms)}.`;

export const getSettledElsewhereWarning = (
  markets: readonly IMarket[]
): string | null => {
  const settledElsewhere = getSettledElsewhere(markets);

  return settledElsewhere && formatSettledElsewhere(settledElsewhere);
};
